import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  Package,
  AlertTriangle,
  TrendingDown,
  Loader2,
  Search,
  Printer,
  Filter,
  Globe,
  MapPin,
  ChevronRight,
  ChevronDown,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { useRecentOutOfStockReport } from '@/hooks/useRecentOutOfStockReport';
import { useReorderList } from '@/hooks/useReorderList';
import { cn } from '@/lib/utils';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';

type StockFilter = 'all' | 'low_stock' | 'out_of_stock' | 'in_stock';

const CHART_COLORS = [
  'hsl(142, 76%, 36%)',  // green
  'hsl(217, 91%, 60%)',  // blue
  'hsl(25, 95%, 53%)',   // orange
  'hsl(0, 84%, 60%)',    // red
];

const RECENT_OOS_DATE_PRESETS = datePresets('today', 'yesterday', 'week', 'last30', 'month', 'last_month');

interface LocationInventory {
  location_id: string;
  location_name: string;
  quantity_on_hand: number;
  quantity_foh: number;
  quantity_boh: number;
  reorder_point: number | null;
  stock_status: 'in_stock' | 'low_stock' | 'out_of_stock';
  inventory_value: number;
}

interface ProductInventory {
  product_id: string;
  product_name: string;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  unit_cost: number;
  unit_price: number;
  // Totals across all locations
  total_quantity: number;
  total_value: number;
  total_retail: number;
  // Stock status (worst case across locations)
  stock_status: 'in_stock' | 'low_stock' | 'out_of_stock';
  // Location breakdown
  locations: LocationInventory[];
  location_count: number;
}

export default function InventoryReports() {
  const { organization, isAdmin } = useAuth();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Inventory Report' });

  const [activeTab, setActiveTab] = useState<'performance' | 'recent_out_of_stock' | 'replenishment'>('performance');
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Draft filters (editable UI state)
  const [draftLocationId, setDraftLocationId] = useState<string>('all');
  const [draftStockFilter, setDraftStockFilter] = useState<StockFilter>('all');
  const [draftSearchQuery, setDraftSearchQuery] = useState('');
  const [draftCategoryFilter, setDraftCategoryFilter] = useState<string>('all');

  // Applied filters (drive queries + results)
  const [appliedLocationId, setAppliedLocationId] = useState<string>('all');
  const [appliedStockFilter, setAppliedStockFilter] = useState<StockFilter>('all');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [appliedCategoryFilter, setAppliedCategoryFilter] = useState<string>('all');

  // Track expanded products
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const hasPendingFilterChanges = useMemo(() => {
    return (
      draftLocationId !== appliedLocationId ||
      draftStockFilter !== appliedStockFilter ||
      draftCategoryFilter !== appliedCategoryFilter ||
      draftSearchQuery.trim() !== appliedSearchQuery
    );
  }, [
    draftLocationId,
    appliedLocationId,
    draftStockFilter,
    appliedStockFilter,
    draftCategoryFilter,
    appliedCategoryFilter,
    draftSearchQuery,
    appliedSearchQuery,
  ]);

  const appliedAdvancedFilterCount = useMemo(() => {
    return [appliedStockFilter !== 'all'].filter(Boolean).length;
  }, [appliedStockFilter]);

  const applyFilters = useCallback(() => {
    setAppliedLocationId(draftLocationId);
    setAppliedStockFilter(draftStockFilter);
    setAppliedCategoryFilter(draftCategoryFilter);
    setAppliedSearchQuery(draftSearchQuery.trim());
    setExpandedProducts(new Set());
  }, [draftLocationId, draftStockFilter, draftCategoryFilter, draftSearchQuery]);

  // Recent out of stock tab filters
  const [recentDraftLocationId, setRecentDraftLocationId] = useState<string>('all');
  const [recentDraftVendorId, setRecentDraftVendorId] = useState<string>('all');
  const [recentDraftCategoryId, setRecentDraftCategoryId] = useState<string>('all');
  const [recentDraftSearchQuery, setRecentDraftSearchQuery] = useState('');
  const [recentDraftDatePreset, setRecentDraftDatePreset] = useState<string>('week');
  const [recentDraftDateRange, setRecentDraftDateRange] = useState(() => {
    const { start, end } = presetValue('week');
    return { start, end };
  });

  const [recentAppliedLocationId, setRecentAppliedLocationId] = useState<string>('all');
  const [recentAppliedVendorId, setRecentAppliedVendorId] = useState<string>('all');
  const [recentAppliedCategoryId, setRecentAppliedCategoryId] = useState<string>('all');
  const [recentAppliedSearchQuery, setRecentAppliedSearchQuery] = useState('');
  const [recentAppliedDatePreset, setRecentAppliedDatePreset] = useState<string>('week');
  const [recentAppliedDateRange, setRecentAppliedDateRange] = useState(() => {
    const { start, end } = presetValue('week');
    return { start, end };
  });

  const recentHasPendingFilterChanges = useMemo(() => {
    return (
      recentDraftLocationId !== recentAppliedLocationId ||
      recentDraftVendorId !== recentAppliedVendorId ||
      recentDraftCategoryId !== recentAppliedCategoryId ||
      recentDraftSearchQuery.trim() !== recentAppliedSearchQuery ||
      recentDraftDatePreset !== recentAppliedDatePreset ||
      recentDraftDateRange.start.getTime() !== recentAppliedDateRange.start.getTime() ||
      recentDraftDateRange.end.getTime() !== recentAppliedDateRange.end.getTime()
    );
  }, [
    recentDraftLocationId,
    recentAppliedLocationId,
    recentDraftVendorId,
    recentAppliedVendorId,
    recentDraftCategoryId,
    recentAppliedCategoryId,
    recentDraftSearchQuery,
    recentAppliedSearchQuery,
    recentDraftDatePreset,
    recentAppliedDatePreset,
    recentDraftDateRange,
    recentAppliedDateRange,
  ]);

  const recentAppliedAdvancedFilterCount = useMemo(() => {
    return [recentAppliedVendorId !== 'all'].filter(Boolean).length;
  }, [recentAppliedVendorId]);

  const applyRecentOutOfStockFilters = useCallback(() => {
    setRecentAppliedLocationId(recentDraftLocationId);
    setRecentAppliedVendorId(recentDraftVendorId);
    setRecentAppliedCategoryId(recentDraftCategoryId);
    setRecentAppliedSearchQuery(recentDraftSearchQuery.trim());
    setRecentAppliedDatePreset(recentDraftDatePreset);
    setRecentAppliedDateRange(recentDraftDateRange);
  }, [
    recentDraftLocationId,
    recentDraftVendorId,
    recentDraftCategoryId,
    recentDraftSearchQuery,
    recentDraftDatePreset,
    recentDraftDateRange,
  ]);

  const recentOutOfStockQueryFilters = useMemo(() => {
    return {
      locationId: recentAppliedLocationId === 'all' ? undefined : recentAppliedLocationId,
      vendorId: recentAppliedVendorId === 'all' ? undefined : recentAppliedVendorId,
      categoryId: recentAppliedCategoryId === 'all' ? undefined : recentAppliedCategoryId,
      dateFrom: recentAppliedDateRange.start.toISOString(),
      dateTo: recentAppliedDateRange.end.toISOString(),
    };
  }, [
    recentAppliedLocationId,
    recentAppliedVendorId,
    recentAppliedCategoryId,
    recentAppliedDateRange,
  ]);

  const {
    rows: recentOutOfStockRows,
    isLoading: recentOutOfStockLoading,
    error: recentOutOfStockError,
    summary: recentOutOfStockSummary,
  } = useRecentOutOfStockReport(recentOutOfStockQueryFilters, {
    enabled: activeTab === 'recent_out_of_stock',
  });

  const filteredRecentOutOfStockRows = useMemo(() => {
    if (!recentAppliedSearchQuery) return recentOutOfStockRows;
    const q = recentAppliedSearchQuery.toLowerCase();
    return recentOutOfStockRows.filter((row) => {
      return (
        row.product_name.toLowerCase().includes(q) ||
        (row.sku && row.sku.toLowerCase().includes(q)) ||
        row.location_name.toLowerCase().includes(q)
      );
    });
  }, [recentOutOfStockRows, recentAppliedSearchQuery]);

  // Replenishment tab filters
  const [replenDraftLocationId, setReplenDraftLocationId] = useState<string>('all');
  const [replenDraftVendorId, setReplenDraftVendorId] = useState<string>('all');
  const [replenDraftCategoryId, setReplenDraftCategoryId] = useState<string>('all');
  const [replenDraftSearchQuery, setReplenDraftSearchQuery] = useState('');

  const [replenAppliedLocationId, setReplenAppliedLocationId] = useState<string>('all');
  const [replenAppliedVendorId, setReplenAppliedVendorId] = useState<string>('all');
  const [replenAppliedCategoryId, setReplenAppliedCategoryId] = useState<string>('all');
  const [replenAppliedSearchQuery, setReplenAppliedSearchQuery] = useState('');

  const replenHasPendingFilterChanges = useMemo(() => {
    return (
      replenDraftLocationId !== replenAppliedLocationId ||
      replenDraftVendorId !== replenAppliedVendorId ||
      replenDraftCategoryId !== replenAppliedCategoryId ||
      replenDraftSearchQuery.trim() !== replenAppliedSearchQuery
    );
  }, [
    replenDraftLocationId,
    replenAppliedLocationId,
    replenDraftVendorId,
    replenAppliedVendorId,
    replenDraftCategoryId,
    replenAppliedCategoryId,
    replenDraftSearchQuery,
    replenAppliedSearchQuery,
  ]);

  const replenAppliedAdvancedFilterCount = useMemo(() => {
    return [replenAppliedVendorId !== 'all'].filter(Boolean).length;
  }, [replenAppliedVendorId]);

  const applyReplenishmentFilters = useCallback(() => {
    setReplenAppliedLocationId(replenDraftLocationId);
    setReplenAppliedVendorId(replenDraftVendorId);
    setReplenAppliedCategoryId(replenDraftCategoryId);
    setReplenAppliedSearchQuery(replenDraftSearchQuery.trim());
  }, [replenDraftLocationId, replenDraftVendorId, replenDraftCategoryId, replenDraftSearchQuery]);

  const {
    reorderItems: replenishmentItems,
    summary: replenishmentSummary,
    isLoading: replenishmentLoading,
    error: replenishmentError,
  } = useReorderList(
    {
      locationId: replenAppliedLocationId === 'all' ? undefined : replenAppliedLocationId,
      vendorId: replenAppliedVendorId === 'all' ? undefined : replenAppliedVendorId,
      categoryId: replenAppliedCategoryId === 'all' ? undefined : replenAppliedCategoryId,
    },
    { enabled: activeTab === 'replenishment' }
  );

  const filteredReplenishmentItems = useMemo(() => {
    if (!replenAppliedSearchQuery) return replenishmentItems;
    const q = replenAppliedSearchQuery.toLowerCase();
    return replenishmentItems.filter((item) => {
      return (
        item.product_name.toLowerCase().includes(q) ||
        (item.sku && item.sku.toLowerCase().includes(q)) ||
        item.location_name.toLowerCase().includes(q) ||
        (item.vendor_name && item.vendor_name.toLowerCase().includes(q))
      );
    });
  }, [replenishmentItems, replenAppliedSearchQuery]);

  // Fetch inventory data across all locations
  const { data: inventoryData, isLoading } = useQuery({
    queryKey: ['inventory-report-all', organization?.id, appliedLocationId],
    queryFn: async () => {
      if (!organization?.id) return null;

      // Build query for inventory items with product and location info
      let query = supabase
        .from('inventory_items')
        .select(`
          id,
          location_id,
          quantity_on_hand,
          quantity_foh,
          quantity_boh,
          reorder_point,
          product:products(
            id,
            name,
            sku,
            price,
            cost,
            low_stock_threshold,
            category:categories(id, name)
          ),
          location:locations!inner(id, name, organization_id)
        `)
        .eq('location.organization_id', organization.id);

      // Filter by location if specific one selected
      if (appliedLocationId !== 'all') {
        query = query.eq('location_id', appliedLocationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!organization?.id,
  });

  // Fetch categories for filtering
  const { data: categories } = useQuery({
    queryKey: ['categories-org', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      // categories are location-scoped (no organization_id column); resolve the
      // org's categories through their location.
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, location:locations!inner(organization_id)')
        .eq('location.organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch suppliers for filtering
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-org', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Group inventory by product with location breakdown
  const groupedProducts = useMemo(() => {
    if (!inventoryData) return [];

    const productMap = new Map<string, ProductInventory>();

    for (const item of inventoryData) {
      const product = item.product as any;
      const location = item.location as any;
      if (!product || !location) continue;

      const threshold = product.low_stock_threshold || item.reorder_point || 10;
      const available = item.quantity_foh ?? item.quantity_on_hand;

      let stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock' = 'in_stock';
      if (available <= 0) {
        stockStatus = 'out_of_stock';
      } else if (available <= threshold) {
        stockStatus = 'low_stock';
      }

      const locationData: LocationInventory = {
        location_id: item.location_id,
        location_name: location.name,
        quantity_on_hand: item.quantity_on_hand || 0,
        quantity_foh: item.quantity_foh ?? item.quantity_on_hand ?? 0,
        quantity_boh: item.quantity_boh || 0,
        reorder_point: item.reorder_point,
        stock_status: stockStatus,
        inventory_value: (product.cost || 0) * (item.quantity_on_hand || 0),
      };

      const existing = productMap.get(product.id);
      if (existing) {
        existing.locations.push(locationData);
        existing.total_quantity += item.quantity_on_hand || 0;
        existing.total_value += locationData.inventory_value;
        existing.total_retail += (product.price || 0) * (item.quantity_on_hand || 0);
        existing.location_count++;
        // Update stock status to worst case
        if (stockStatus === 'out_of_stock') {
          existing.stock_status = 'out_of_stock';
        } else if (stockStatus === 'low_stock' && existing.stock_status !== 'out_of_stock') {
          existing.stock_status = 'low_stock';
        }
      } else {
        productMap.set(product.id, {
          product_id: product.id,
          product_name: product.name,
          sku: product.sku,
          category_id: product.category?.id || null,
          category_name: product.category?.name || null,
          unit_cost: product.cost || 0,
          unit_price: product.price || 0,
          total_quantity: item.quantity_on_hand || 0,
          total_value: locationData.inventory_value,
          total_retail: (product.price || 0) * (item.quantity_on_hand || 0),
          stock_status: stockStatus,
          locations: [locationData],
          location_count: 1,
        });
      }
    }

    return Array.from(productMap.values());
  }, [inventoryData]);

  // Apply filters
  const filteredProducts = useMemo(() => {
    let products = groupedProducts;

    // Stock filter
    if (appliedStockFilter !== 'all') {
      products = products.filter(p => p.stock_status === appliedStockFilter);
    }

    // Category filter
    if (appliedCategoryFilter !== 'all') {
      products = products.filter(p => p.category_id === appliedCategoryFilter);
    }

    // Search filter
    if (appliedSearchQuery) {
      const query = appliedSearchQuery.toLowerCase();
      products = products.filter(
        p => p.product_name.toLowerCase().includes(query) ||
             (p.sku && p.sku.toLowerCase().includes(query))
      );
    }

    // Sort by stock status (out of stock first, then low stock)
    return products.sort((a, b) => {
      const statusOrder = { out_of_stock: 0, low_stock: 1, in_stock: 2 };
      return statusOrder[a.stock_status] - statusOrder[b.stock_status];
    });
  }, [groupedProducts, appliedStockFilter, appliedCategoryFilter, appliedSearchQuery]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalItems = groupedProducts.length;
    const outOfStock = groupedProducts.filter(p => p.stock_status === 'out_of_stock').length;
    const lowStock = groupedProducts.filter(p => p.stock_status === 'low_stock').length;
    const inStock = totalItems - outOfStock - lowStock;
    const totalValue = groupedProducts.reduce((sum, p) => sum + p.total_value, 0);
    const totalRetail = groupedProducts.reduce((sum, p) => sum + p.total_retail, 0);

    return {
      totalItems,
      outOfStock,
      lowStock,
      inStock,
      totalValue,
      totalRetail,
      potentialProfit: totalRetail - totalValue,
    };
  }, [groupedProducts]);

  // Toggle product expansion
  const toggleExpand = useCallback((productId: string) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  // Expand/collapse all
  const expandAll = useCallback(() => {
    setExpandedProducts(new Set(filteredProducts.map(p => p.product_id)));
  }, [filteredProducts]);

  const collapseAll = useCallback(() => {
    setExpandedProducts(new Set());
  }, []);

  // Chart data for stock status distribution
  const stockStatusChartData = useMemo(() => {
    return [
      { name: 'In Stock', value: summaryStats.inStock, fill: CHART_COLORS[0] },
      { name: 'Low Stock', value: summaryStats.lowStock, fill: CHART_COLORS[2] },
      { name: 'Out of Stock', value: summaryStats.outOfStock, fill: CHART_COLORS[3] },
    ].filter(d => d.value > 0);
  }, [summaryStats]);

  // Chart data for top categories by value
  const categoryValueChart = useMemo(() => {
    const categoryMap = new Map<string, number>();
    groupedProducts.forEach(product => {
      const categoryName = product.category_name || 'Uncategorized';
      const current = categoryMap.get(categoryName) || 0;
      categoryMap.set(categoryName, current + product.total_value);
    });

    return Array.from(categoryMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [groupedProducts]);

  const chartConfig: ChartConfig = {
    value: { label: 'Value', color: CHART_COLORS[0] },
  };

  // Export CSV (per tab)
  const exportPerformanceToCSV = () => {
    if (!inventoryData) return;

    const headers = ['Product', 'SKU', 'Location', 'On Hand', 'FOH', 'BOH', 'Status', 'Unit Cost', 'Value'];
    const rows: (string | number)[][] = [];

    for (const product of filteredProducts) {
      for (const loc of product.locations) {
        rows.push([
          product.product_name,
          product.sku || '',
          loc.location_name,
          loc.quantity_on_hand,
          loc.quantity_foh,
          loc.quantity_boh,
          loc.stock_status.replace('_', ' '),
          product.unit_cost.toFixed(2),
          loc.inventory_value.toFixed(2),
        ]);
      }
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-performance-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportRecentOutOfStockToCSV = () => {
    const headers = [
      'Product',
      'SKU',
      'Outlet',
      'Last stockout',
      'Current FOH',
      'Current BOH',
      'Current total',
      'Items sold',
      'In transit',
      'On order',
      'Last sale',
    ];

    const rows: (string | number)[][] = filteredRecentOutOfStockRows.map((row) => [
      row.product_name,
      row.sku || '',
      row.location_name,
      row.last_stockout_at ? new Date(row.last_stockout_at).toLocaleString() : '',
      row.current_quantity_foh ?? 0,
      row.current_quantity_boh ?? 0,
      row.current_quantity_on_hand ?? 0,
      row.items_sold ?? 0,
      row.in_transit ?? 0,
      row.on_order ?? 0,
      row.last_sale_at ? new Date(row.last_sale_at).toLocaleString() : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-recent-out-of-stock-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportReplenishmentToCSV = () => {
    const headers = [
      'Product',
      'SKU',
      'Category',
      'Supplier',
      'Outlet',
      'QOH',
      'FOH',
      'BOH',
      'In transit',
      'On order',
      'Reorder point',
      'Desired level',
      'Need to order',
      'Last cost',
    ];

    const rows: (string | number)[][] = filteredReplenishmentItems.map((item) => [
      item.product_name,
      item.sku || '',
      item.category_name || '',
      item.vendor_name || '',
      item.location_name,
      item.quantity_on_hand ?? 0,
      item.quantity_foh ?? 0,
      item.quantity_boh ?? 0,
      item.in_transit ?? 0,
      item.on_order ?? 0,
      item.reorder_point ?? 0,
      item.desired_level ?? 0,
      item.need_to_order ?? 0,
      (item.last_cost ?? 0).toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-replenishment-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    if (activeTab === 'recent_out_of_stock') {
      exportRecentOutOfStockToCSV();
      return;
    }

    if (activeTab === 'replenishment') {
      exportReplenishmentToCSV();
      return;
    }

    // Default to Performance export.
    exportPerformanceToCSV();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'out_of_stock':
        return <Badge variant="destructive">Out of Stock</Badge>;
      case 'low_stock':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Low Stock</Badge>;
      default:
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">In Stock</Badge>;
    }
  };

  // Format value or show dash for zero/null
  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined || value === 0) {
      return <span className="text-muted-foreground">-</span>;
    }
    return value.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Inventory Report</h1>
          <p className="text-sm text-muted-foreground">
            Track stock levels by product and location
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as typeof activeTab)}
        className="w-full"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="recent_out_of_stock">Recent out of stock items</TabsTrigger>
            <TabsTrigger value="replenishment">Replenishment</TabsTrigger>
          </TabsList>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              disabled
              title="Custom views are coming soon."
            >
              Custom views
            </Button>
          )}
        </div>

        <TabsContent value="performance" className="mt-4 space-y-6">
          {/* Summary (collapsed by default) */}
          <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-base font-semibold">Summary</h2>
                <p className="text-sm text-muted-foreground">
                  Inventory totals and charts
                </p>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  {summaryOpen ? 'Hide summary' : 'Show summary'}
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', summaryOpen && 'rotate-180')}
                  />
                </Button>
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent className="pt-4 space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Total Products</span>
                      <Package className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold mt-2">
                      {isLoading ? '...' : summaryStats.totalItems}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Low Stock Items</span>
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold mt-2 text-amber-600">
                      {isLoading ? '...' : summaryStats.lowStock}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Out of Stock</span>
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    </div>
                    <p className="text-2xl font-bold mt-2 text-red-600">
                      {isLoading ? '...' : summaryStats.outOfStock}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Inventory Value</span>
                      <Package className="h-4 w-4 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold mt-2">
                      {isLoading ? '...' : formatCurrency(summaryStats.totalValue)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Retail: {formatCurrency(summaryStats.totalRetail)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              {isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Stock Status Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Stock Status Distribution</CardTitle>
                      <CardDescription>Overview of inventory health</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {stockStatusChartData.length > 0 ? (
                        <ChartContainer config={chartConfig} className="h-[250px]">
                          <PieChart>
                            <ChartTooltip
                              content={
                                <ChartTooltipContent
                                  valueFormatter={(value) => `${value} items`}
                                />
                              }
                            />
                            <Pie
                              data={stockStatusChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={2}
                              dataKey="value"
                              nameKey="name"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              labelLine={false}
                            >
                              {stockStatusChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ChartContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                          No inventory data
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Inventory Value by Category */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Value by Category</CardTitle>
                      <CardDescription>Top 10 categories by inventory value</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {categoryValueChart.length > 0 ? (
                        <ChartContainer config={chartConfig} className="h-[250px]">
                          <BarChart data={categoryValueChart} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis
                              type="number"
                              fontSize={12}
                              tickLine={false}
                              axisLine={false}
                              tickFormatter={(value) => formatCurrency(value)}
                            />
                            <YAxis
                              dataKey="name"
                              type="category"
                              fontSize={12}
                              tickLine={false}
                              axisLine={false}
                              width={100}
                            />
                            <ChartTooltip
                              content={
                                <ChartTooltipContent
                                  valueFormatter={(value) => formatCurrency(value)}
                                />
                              }
                            />
                            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ChartContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                          No category data
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Location Selector */}
            <Select value={draftLocationId} onValueChange={setDraftLocationId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5" />
                    <span>All Locations</span>
                  </div>
                </SelectItem>
                {locations.length > 0 && <div className="my-1 h-px bg-border" />}
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{loc.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or SKU..."
                value={draftSearchQuery}
                onChange={(e) => setDraftSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyFilters();
                  }
                }}
                className="pl-9"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Select value={draftCategoryFilter} onValueChange={setDraftCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories?.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                size="sm"
                className="gap-2"
                variant={hasPendingFilterChanges ? 'default' : 'outline'}
                onClick={applyFilters}
              >
                <Search className="h-4 w-4" />
                Search
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Filter className="h-4 w-4" />
                    More filters{appliedAdvancedFilterCount > 0 ? ` (${appliedAdvancedFilterCount})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Stock status</div>
                      <Select value={draftStockFilter} onValueChange={(v) => setDraftStockFilter(v as StockFilter)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Items</SelectItem>
                          <SelectItem value="in_stock">In Stock</SelectItem>
                          <SelectItem value="low_stock">Low Stock</SelectItem>
                          <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDraftStockFilter('all')}
                        disabled={draftStockFilter === 'all'}
                      >
                        Clear
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Click Search to apply filters.
                      </span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Inventory Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Inventory Details</CardTitle>
                <CardDescription>
                  {filteredProducts.length} products
                  {appliedStockFilter !== 'all' && ` (filtered by ${appliedStockFilter.replace('_', ' ')})`}
                  {appliedLocationId === 'all' && locations.length > 1 && (
                    <span className="ml-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3.5 w-3.5 inline text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Click a product row to see location breakdown</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                  )}
                </CardDescription>
              </div>
              {appliedLocationId === 'all' && locations.length > 1 && (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={expandAll}>
                    Expand All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={collapseAll}>
                    Collapse All
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="w-[300px]">
                            <div className="flex items-center gap-2">
                              <span>SKU name</span>
                              <span className="text-muted-foreground font-normal">Outlet</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center border-l">On Hand</TableHead>
                          <TableHead className="text-center">FOH</TableHead>
                          <TableHead className="text-center">BOH</TableHead>
                          <TableHead className="text-center border-l">Status</TableHead>
                          <TableHead className="text-right border-l">Unit Cost</TableHead>
                          <TableHead className="text-right border-l">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProducts.slice(0, 100).map((product) => {
                          const isExpanded = expandedProducts.has(product.product_id);
                          const hasMultipleLocations = product.location_count > 1;
                          const canExpand = appliedLocationId === 'all' && hasMultipleLocations;

                          return (
                            <>
                              {/* Product Row */}
                              <TableRow
                                key={product.product_id}
                                className={cn(
                                  'group',
                                  canExpand && 'cursor-pointer hover:bg-muted/50',
                                  product.stock_status === 'out_of_stock' && 'bg-red-50 dark:bg-red-950/20'
                                )}
                                onClick={() => canExpand && toggleExpand(product.product_id)}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {canExpand ? (
                                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                                        {isExpanded ? (
                                          <ChevronDown className="h-4 w-4" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4" />
                                        )}
                                      </Button>
                                    ) : (
                                      <div className="w-6" />
                                    )}
                                    <Checkbox className="shrink-0" onClick={(e) => e.stopPropagation()} />
                                    <div className="min-w-0">
                                      <Link
                                        to={`/stock-control/overview?product=${product.product_id}`}
                                        className="font-medium truncate text-primary hover:underline block"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {product.product_name}
                                      </Link>
                                      {product.sku && (
                                        <div className="text-xs text-muted-foreground">{product.sku}</div>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center tabular-nums border-l font-medium">
                                  {product.total_quantity}
                                </TableCell>
                                <TableCell className="text-center tabular-nums">
                                  {formatValue(product.locations.reduce((sum, l) => sum + l.quantity_foh, 0))}
                                </TableCell>
                                <TableCell className="text-center tabular-nums">
                                  {formatValue(product.locations.reduce((sum, l) => sum + l.quantity_boh, 0))}
                                </TableCell>
                                <TableCell className="text-center border-l">
                                  {getStatusBadge(product.stock_status)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums border-l">
                                  {formatCurrency(product.unit_cost)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums font-medium border-l">
                                  {formatCurrency(product.total_value)}
                                </TableCell>
                              </TableRow>

                              {/* Location Rows (when expanded) */}
                              {isExpanded && product.locations.map((location) => (
                                <TableRow
                                  key={`${product.product_id}-${location.location_id}`}
                                  className="bg-muted/20 hover:bg-muted/30"
                                >
                                  <TableCell>
                                    <div className="flex items-center gap-2 pl-14">
                                      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                      <span className="text-sm text-muted-foreground">
                                        {location.location_name}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center tabular-nums border-l">
                                    {location.quantity_on_hand}
                                  </TableCell>
                                  <TableCell className="text-center tabular-nums">
                                    {formatValue(location.quantity_foh)}
                                  </TableCell>
                                  <TableCell className="text-center tabular-nums">
                                    {formatValue(location.quantity_boh)}
                                  </TableCell>
                                  <TableCell className="text-center border-l">
                                    {getStatusBadge(location.stock_status)}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums border-l">
                                    {formatCurrency(product.unit_cost)}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums border-l">
                                    {formatCurrency(location.inventory_value)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </>
                          );
                        })}
                        {filteredProducts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                              No inventory items found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  {filteredProducts.length > 100 && (
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      Showing first 100 of {filteredProducts.length} products
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recent_out_of_stock" className="mt-4 space-y-6">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={recentDraftLocationId} onValueChange={setRecentDraftLocationId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5" />
                    <span>All Locations</span>
                  </div>
                </SelectItem>
                {locations.length > 0 && <div className="my-1 h-px bg-border" />}
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{loc.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DateRangeSelect
              presets={RECENT_OOS_DATE_PRESETS}
              value={{
                preset: recentDraftDatePreset,
                start: recentDraftDateRange.start,
                end: recentDraftDateRange.end,
              }}
              onChange={(v: DateRangeValue) => {
                setRecentDraftDatePreset(v.preset);
                setRecentDraftDateRange({ start: v.start, end: v.end });
              }}
            />

            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or SKU..."
                value={recentDraftSearchQuery}
                onChange={(e) => setRecentDraftSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyRecentOutOfStockFilters();
                  }
                }}
                className="pl-9"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Select value={recentDraftCategoryId} onValueChange={setRecentDraftCategoryId}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories?.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                size="sm"
                className="gap-2"
                variant={recentHasPendingFilterChanges ? 'default' : 'outline'}
                onClick={applyRecentOutOfStockFilters}
              >
                <Search className="h-4 w-4" />
                Search
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Filter className="h-4 w-4" />
                    More filters{recentAppliedAdvancedFilterCount > 0 ? ` (${recentAppliedAdvancedFilterCount})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Supplier</div>
                      <Select value={recentDraftVendorId} onValueChange={setRecentDraftVendorId}>
                        <SelectTrigger>
                          <SelectValue placeholder="All suppliers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All suppliers</SelectItem>
                          {suppliers?.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRecentDraftVendorId('all')}
                        disabled={recentDraftVendorId === 'all'}
                      >
                        Clear
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Click Search to apply filters.
                      </span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Report Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent out of stock items</CardTitle>
              <CardDescription>
                {filteredRecentOutOfStockRows.length} rows
                <span className="ml-2">
                  {recentAppliedDateRange.start.toLocaleDateString()} - {recentAppliedDateRange.end.toLocaleDateString()}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recentOutOfStockLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : recentOutOfStockError ? (
                <div className="py-10 text-center text-muted-foreground">
                  Unable to load report data.
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="min-w-[280px]">SKU name</TableHead>
                          <TableHead className="min-w-[180px]">Outlet</TableHead>
                          <TableHead className="min-w-[180px]">Last stockout</TableHead>
                          <TableHead className="text-right min-w-[110px]">Current FOH</TableHead>
                          <TableHead className="text-right min-w-[110px]">Current Total</TableHead>
                          <TableHead className="text-right min-w-[110px]">Items sold</TableHead>
                          <TableHead className="text-right min-w-[110px]">Inbound</TableHead>
                          <TableHead className="text-right min-w-[110px]">On order</TableHead>
                          <TableHead className="min-w-[180px]">Last sale</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRecentOutOfStockRows.slice(0, 200).map((row) => (
                          <TableRow key={`${row.product_id}-${row.location_id}`}>
                            <TableCell>
                              <div className="min-w-0">
                                <Link
                                  to={`/stock-control/overview?product=${row.product_id}`}
                                  className="font-medium truncate text-primary hover:underline block"
                                >
                                  {row.product_name}
                                </Link>
                                {row.sku && (
                                  <div className="text-xs text-muted-foreground">{row.sku}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {row.location_name}
                            </TableCell>
                            <TableCell className="text-sm">
                              {row.last_stockout_at ? new Date(row.last_stockout_at).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-medium">
                              {(row.current_quantity_foh ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(row.current_quantity_on_hand ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(row.items_sold ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(row.in_transit ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(row.on_order ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-sm">
                              {row.last_sale_at ? new Date(row.last_sale_at).toLocaleString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredRecentOutOfStockRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                              No stockout events found for this range.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {filteredRecentOutOfStockRows.length > 200 && (
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      Showing first 200 of {filteredRecentOutOfStockRows.length} rows. Use Export CSV for full results.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="replenishment" className="mt-4 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Items to Reorder</span>
                  <Package className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold mt-2">
                  {replenishmentLoading ? '...' : replenishmentSummary.totalItems}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Need</span>
                  <TrendingDown className="h-4 w-4 text-amber-500" />
                </div>
                <p className="text-2xl font-bold mt-2">
                  {replenishmentLoading ? '...' : replenishmentSummary.totalNeed.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Estimated Cost</span>
                  <Package className="h-4 w-4 text-green-500" />
                </div>
                <p className="text-2xl font-bold mt-2">
                  {replenishmentLoading ? '...' : formatCurrency(replenishmentSummary.totalValue)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Negative Items</span>
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                </div>
                <p className="text-2xl font-bold mt-2 text-red-600">
                  {replenishmentLoading ? '...' : replenishmentSummary.negativeItems}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={replenDraftLocationId} onValueChange={setReplenDraftLocationId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5" />
                    <span>All Locations</span>
                  </div>
                </SelectItem>
                {locations.length > 0 && <div className="my-1 h-px bg-border" />}
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{loc.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, SKU, supplier, outlet..."
                value={replenDraftSearchQuery}
                onChange={(e) => setReplenDraftSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyReplenishmentFilters();
                  }
                }}
                className="pl-9"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Select value={replenDraftCategoryId} onValueChange={setReplenDraftCategoryId}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories?.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                size="sm"
                className="gap-2"
                variant={replenHasPendingFilterChanges ? 'default' : 'outline'}
                onClick={applyReplenishmentFilters}
              >
                <Search className="h-4 w-4" />
                Search
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Filter className="h-4 w-4" />
                    More filters{replenAppliedAdvancedFilterCount > 0 ? ` (${replenAppliedAdvancedFilterCount})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Supplier</div>
                      <Select value={replenDraftVendorId} onValueChange={setReplenDraftVendorId}>
                        <SelectTrigger>
                          <SelectValue placeholder="All suppliers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All suppliers</SelectItem>
                          {suppliers?.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplenDraftVendorId('all')}
                        disabled={replenDraftVendorId === 'all'}
                      >
                        Clear
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Click Search to apply filters.
                      </span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Report Table */}
          <Card>
            <CardHeader>
              <CardTitle>Replenishment</CardTitle>
              <CardDescription>
                {filteredReplenishmentItems.length} items
              </CardDescription>
            </CardHeader>
            <CardContent>
              {replenishmentLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : replenishmentError ? (
                <div className="py-10 text-center text-muted-foreground">
                  Unable to load report data.
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="min-w-[280px]">SKU name</TableHead>
                          <TableHead className="min-w-[180px]">Supplier</TableHead>
                          <TableHead className="min-w-[180px]">Outlet</TableHead>
                          <TableHead className="text-right min-w-[110px]">Current FOH</TableHead>
                          <TableHead className="text-right min-w-[110px]">Inbound</TableHead>
                          <TableHead className="text-right min-w-[110px]">On order</TableHead>
                          <TableHead className="text-right min-w-[110px]">Reorder</TableHead>
                          <TableHead className="text-right min-w-[110px]">Desired</TableHead>
                          <TableHead className="text-right min-w-[110px]">Need</TableHead>
                          <TableHead className="text-right min-w-[130px]">Est. Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredReplenishmentItems.slice(0, 200).map((item) => (
                          <TableRow key={`${item.product_id}-${item.location_id}`}>
                            <TableCell>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <Link
                                    to={`/stock-control/overview?product=${item.product_id}`}
                                    className="font-medium truncate text-primary hover:underline block"
                                  >
                                    {item.product_name}
                                  </Link>
                                  {item.is_negative && (
                                    <Badge variant="destructive">Negative</Badge>
                                  )}
                                </div>
                                {item.sku && (
                                  <div className="text-xs text-muted-foreground">{item.sku}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {item.vendor_name || '-'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {item.location_name}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-medium">
                              {(item.quantity_foh ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(item.in_transit ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(item.on_order ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(item.reorder_point ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {(item.desired_level ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-medium">
                              {(item.need_to_order ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrency((item.need_to_order ?? 0) * (item.last_cost ?? 0))}
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredReplenishmentItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-10 text-muted-foreground">
                              No items found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {filteredReplenishmentItems.length > 200 && (
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      Showing first 200 of {filteredReplenishmentItems.length} items. Use Export CSV for full results.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Print template */}
      <div ref={printRef} className="print-source">
        {activeTab === 'performance' && (
          <>
            <div className="print-header">
              <div>
                <h1>Inventory Report - Performance</h1>
                <div className="print-meta">
                  {appliedLocationId === 'all'
                    ? 'All Locations'
                    : locations.find(l => l.id === appliedLocationId)?.name}
                </div>
              </div>
              <div className="print-meta">
                Generated on {new Date().toLocaleString()}
              </div>
            </div>

            <div className="print-summary">
              <div className="print-card">
                <div className="label">Total Products</div>
                <div className="value">{summaryStats.totalItems}</div>
              </div>
              <div className="print-card">
                <div className="label">Low Stock</div>
                <div className="value">{summaryStats.lowStock}</div>
              </div>
              <div className="print-card">
                <div className="label">Out of Stock</div>
                <div className="value">{summaryStats.outOfStock}</div>
              </div>
              <div className="print-card">
                <div className="label">Inventory Value</div>
                <div className="value">{formatCurrency(summaryStats.totalValue)}</div>
              </div>
            </div>

            <div className="print-section">
              <h2>Inventory Details</h2>
              {filteredProducts.length > 0 ? (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Location</th>
                      <th>On Hand</th>
                      <th>FOH</th>
                      <th>BOH</th>
                      <th>Status</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.flatMap(product =>
                      product.locations.map(location => (
                        <tr key={`${product.product_id}-${location.location_id}`}>
                          <td>{product.product_name}</td>
                          <td>{location.location_name}</td>
                          <td>{location.quantity_on_hand}</td>
                          <td>{location.quantity_foh}</td>
                          <td>{location.quantity_boh}</td>
                          <td>{location.stock_status.replace('_', ' ')}</td>
                          <td>{formatCurrency(location.inventory_value)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                <div className="print-meta">No inventory items found.</div>
              )}
            </div>
          </>
        )}

        {activeTab === 'recent_out_of_stock' && (
          <>
            <div className="print-header">
              <div>
                <h1>Inventory Report - Recent out of stock items</h1>
                <div className="print-meta">
                  {(recentAppliedLocationId === 'all'
                    ? 'All Locations'
                    : locations.find(l => l.id === recentAppliedLocationId)?.name) || 'All Locations'}
                  <span className="ml-2">
                    {recentAppliedDateRange.start.toLocaleDateString()} - {recentAppliedDateRange.end.toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="print-meta">
                Generated on {new Date().toLocaleString()}
              </div>
            </div>

            <div className="print-summary">
              <div className="print-card">
                <div className="label">Rows</div>
                <div className="value">{filteredRecentOutOfStockRows.length}</div>
              </div>
              <div className="print-card">
                <div className="label">Items sold</div>
                <div className="value">{recentOutOfStockSummary.totalItemsSold}</div>
              </div>
              <div className="print-card">
                <div className="label">Inbound</div>
                <div className="value">{recentOutOfStockSummary.totalInTransit}</div>
              </div>
              <div className="print-card">
                <div className="label">On order</div>
                <div className="value">{recentOutOfStockSummary.totalOnOrder}</div>
              </div>
            </div>

            <div className="print-section">
              <h2>Details</h2>
              {filteredRecentOutOfStockRows.length > 0 ? (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Outlet</th>
                      <th>Last stockout</th>
                      <th>Current FOH</th>
                      <th>Current Total</th>
                      <th>Items sold</th>
                      <th>Inbound</th>
                      <th>On order</th>
                      <th>Last sale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecentOutOfStockRows.map((row) => (
                      <tr key={`${row.product_id}-${row.location_id}`}>
                        <td>{row.product_name}</td>
                        <td>{row.location_name}</td>
                        <td>{row.last_stockout_at ? new Date(row.last_stockout_at).toLocaleString() : ''}</td>
                        <td>{row.current_quantity_foh}</td>
                        <td>{row.current_quantity_on_hand}</td>
                        <td>{row.items_sold}</td>
                        <td>{row.in_transit}</td>
                        <td>{row.on_order}</td>
                        <td>{row.last_sale_at ? new Date(row.last_sale_at).toLocaleString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="print-meta">No stockout events found.</div>
              )}
            </div>
          </>
        )}

        {activeTab === 'replenishment' && (
          <>
            <div className="print-header">
              <div>
                <h1>Inventory Report - Replenishment</h1>
                <div className="print-meta">
                  {(replenAppliedLocationId === 'all'
                    ? 'All Locations'
                    : locations.find(l => l.id === replenAppliedLocationId)?.name) || 'All Locations'}
                </div>
              </div>
              <div className="print-meta">
                Generated on {new Date().toLocaleString()}
              </div>
            </div>

            <div className="print-summary">
              <div className="print-card">
                <div className="label">Items to Reorder</div>
                <div className="value">{filteredReplenishmentItems.length}</div>
              </div>
              <div className="print-card">
                <div className="label">Total Need</div>
                <div className="value">{replenishmentSummary.totalNeed}</div>
              </div>
              <div className="print-card">
                <div className="label">Estimated Cost</div>
                <div className="value">{formatCurrency(replenishmentSummary.totalValue)}</div>
              </div>
              <div className="print-card">
                <div className="label">Negative Items</div>
                <div className="value">{replenishmentSummary.negativeItems}</div>
              </div>
            </div>

            <div className="print-section">
              <h2>Details</h2>
              {filteredReplenishmentItems.length > 0 ? (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Supplier</th>
                      <th>Outlet</th>
                      <th>Current FOH</th>
                      <th>Inbound</th>
                      <th>On order</th>
                      <th>Reorder</th>
                      <th>Desired</th>
                      <th>Need</th>
                      <th>Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReplenishmentItems.map((item) => (
                      <tr key={`${item.product_id}-${item.location_id}`}>
                        <td>{item.product_name}</td>
                        <td>{item.vendor_name || ''}</td>
                        <td>{item.location_name}</td>
                        <td>{item.quantity_foh}</td>
                        <td>{item.in_transit}</td>
                        <td>{item.on_order}</td>
                        <td>{item.reorder_point}</td>
                        <td>{item.desired_level}</td>
                        <td>{item.need_to_order}</td>
                        <td>{formatCurrency((item.need_to_order || 0) * (item.last_cost || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="print-meta">No items found.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
